import styled from '@emotion/styled';
import { Image as ImageComponent, useComponentTheme, withSpacingStyles } from '@vmos2/design-system';
import { useSettings } from '@vmos2/providers';
import React, { useEffect } from 'react';

import { LoyaltyLogo } from '../loyaltyLogo';

type Props = {
  height?: string,
  setIsStampImageLoading?: (value: boolean) => void,
  stampCount?: number,
  userPoints?: number,
  width?: string,
};

export const StampCard = withSpacingStyles(
  ({ className, height, setIsStampImageLoading, stampCount, userPoints = 0, width }: Props) => {
    const { loyaltyStampCard } = useSettings();
    const { activeIcon, inactiveIcon } = useComponentTheme(theme => theme.components.LayoutCard, 'Stamps');
    const pointsPerPromotion = loyaltyStampCard?.stampsPerPromo || 0;
    const calculatedNrOfStamps = stampCount
      ? Math.min(stampCount, pointsPerPromotion)
      : Math.round(userPoints % pointsPerPromotion);
    const numberOfStamps = Number.isNaN(calculatedNrOfStamps) ? 0 : calculatedNrOfStamps;
    const maxStamps = Array.from({ length: numberOfStamps }, (stamp, i) => i);
    const stampCardImageUrl = loyaltyStampCard?.stampCardImages?.[numberOfStamps];

    useEffect(() => {
      if (setIsStampImageLoading) {
        if (stampCardImageUrl) {
          const image = new Image();

          image.src = stampCardImageUrl;
          image.onload = () => setIsStampImageLoading(false);
          image.onerror = () => setIsStampImageLoading(false);
        } else {
          setIsStampImageLoading(false);
        }
      }
    }, [setIsStampImageLoading, stampCardImageUrl]);

    if (!loyaltyStampCard?.enabled || !loyaltyStampCard?.stampsPerPromo) {
      return null;
    }

    return (
      <div className={className} data-test="stamp-card">
        {stampCardImageUrl ? (
          <StampCardImage
            alt={numberOfStamps}
            data-test="stamps-image"
            height={height}
            src={stampCardImageUrl}
            width={width}
          />
        ) : (
          <StampCardGrid data-test="stamps-grid">
            {Array.from({ length: pointsPerPromotion }, (point, i) => i).map(point => {
              const isActive = maxStamps.includes(point);

              return (
                <StampCardGridItem key={point}>
                  <StampIcon
                    active={isActive}
                    data-test="stamps-grid-item"
                    image={isActive ? activeIcon : inactiveIcon}
                  >
                    {isActive && !activeIcon ? <LoyaltyLogo size="63%" /> : null}
                  </StampIcon>
                </StampCardGridItem>
              );
            })}
          </StampCardGrid>
        )}
      </div>
    );
  }
);

const StampCardGrid = styled('ul')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  padding-left: 0;
  margin: 0;
`;

const StampCardGridItem = styled('li')`
  display: flex;
  flex: 0 0 33.333333%;
  justify-content: center;
  padding: ${({ theme }) => theme.margins.xs} 0;
  list-style: none;
`;

const StampCardImage = styled(ImageComponent)(({ height, width }) => `
  display: block;
  ${width ? `width: ${width};` : ''};
  max-width: 100%;
  ${height ? `height: ${height};` : ''};
  object-fit: contain;
  margin: 0 auto;
`);

const StampIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background-color: ${({ theme, active }) => (active ? theme.color.brand : theme.color.lightGrey)};
  ${({ image }) => image && `background-image: url(${image});`}
  background-size: cover;
`;

StampCard.displayName = 'StampCard';
